import { Controller } from "@hotwired/stimulus"

// GSM-7 basic character set (3GPP TS 23.038)
const GSM_BASIC = new Set([
  "@", "£", "$", "¥", "è", "é", "ù", "ì", "ò", "Ç", "\n", "Ø", "ø", "\r", "Å", "å",
  "Δ", "_", "Φ", "Γ", "Λ", "Ω", "Π", "Ψ", "Σ", "Θ", "Ξ", "Æ", "æ", "ß", "É",
  " ", "!", "\"", "#", "¤", "%", "&", "'", "(", ")", "*", "+", ",", "-", ".", "/",
  "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", ":", ";", "<", "=", ">", "?",
  "¡", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O",
  "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "Ä", "Ö", "Ñ", "Ü", "§",
  "¿", "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o",
  "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z", "ä", "ö", "ñ", "ü", "à"
])

const GSM_EXTENDED = new Set(["|", "^", "€", "{", "}", "[", "]", "~", "\\"])

export default class extends Controller {
  static targets = ["body", "bubble", "placeholder", "charCount", "segmentCount", "recipientCount", "campus", "submit"]
  static values = {
    recipientCounts: { type: Object, default: {} }
  }

  connect() {
    this.refresh()
  }

  refresh() {
    this.updateBubble()
    this.updateCounts()
    this.updateRecipientCount()
    this.updateConfirm()
  }

  updateBubble() {
    const text = this.bodyTarget.value

    if (text.length === 0) {
      this.bubbleTarget.classList.add("hidden")
      this.placeholderTarget.classList.remove("hidden")
      this.bubbleTarget.textContent = ""
      return
    }

    this.placeholderTarget.classList.add("hidden")
    this.bubbleTarget.classList.remove("hidden")
    this.bubbleTarget.textContent = text
  }

  updateCounts() {
    const text = this.bodyTarget.value
    const encoding = this.isGsm7(text) ? "GSM-7" : "UCS-2"
    const length = encoding === "GSM-7" ? this.gsmLength(text) : [...text].length
    const segments = this.segmentCountFor(text)

    this.charCountTarget.textContent = `${length} character${length === 1 ? "" : "s"} · ${encoding}`
    this.segmentCountTarget.textContent = `${segments} segment${segments === 1 ? "" : "s"}`
  }

  updateRecipientCount() {
    const campusId = this.campusTarget.value
    const count = campusId ? (this.recipientCountsValue[campusId] ?? this.recipientCountsValue[String(campusId)] ?? 0) : 0
    this.recipientCountTarget.textContent = count
  }

  updateConfirm() {
    if (!this.hasSubmitTarget) return

    const campusId = this.campusTarget.value
    const count = campusId ? (this.recipientCountsValue[campusId] ?? this.recipientCountsValue[String(campusId)] ?? 0) : 0
    const label = count === 1 ? "parent" : "parents"
    this.submitTarget.dataset.turboConfirm = `Send this text to ${count} ${label}?`
  }

  isGsm7(text) {
    for (const char of text) {
      if (!GSM_BASIC.has(char) && !GSM_EXTENDED.has(char)) return false
    }
    return true
  }

  gsmLength(text) {
    let length = 0
    for (const char of text) {
      length += GSM_EXTENDED.has(char) ? 2 : 1
    }
    return length
  }

  segmentCountFor(text) {
    if (text.length === 0) return 0

    if (this.isGsm7(text)) {
      const length = this.gsmLength(text)
      if (length <= 160) return 1
      return Math.ceil(length / 153)
    }

    const length = [...text].length
    if (length <= 70) return 1
    return Math.ceil(length / 67)
  }
}
